import React, { useEffect, useRef, useState } from 'react'

import { merge, pathOr } from 'ramda'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import {
  Button,
  Card,
  Flex,
  Header,
  Icon,
  InputText,
  Skeleton,
  StatusMessage,
  styled,
  Text,
} from '@driftt/dds'
import { faAngleRight } from '@driftt/dds.icons/light/faAngleRight'
import { access as ACCESS } from '@driftt/traffic-control'

import { ConferenceProvider } from 'api/conferenceProviders'
import { saveNewMeetingType, updateMeetingType } from 'api/meetings'
import AttendeeSelect from 'components/MeetingEditComponents/AttendeesSelect'
import DurationSelect from 'components/MeetingEditComponents/DurationSelect'
import { GuestEmailSettings } from 'components/MeetingEditComponents/GuestEmailSettings'
import { DEFAULT_GUEST_CONFIG } from 'components/MeetingEditComponents/GuestEmailSettings/constants'
import LanguageSelect from 'components/MeetingEditComponents/LanguageSelect'
import MeetingDescriptionEditor from 'components/MeetingEditComponents/MeetingDescriptionEditor'
import MeetingLinkInput from 'components/MeetingEditComponents/MeetingLinkInput'
import OrgWideWarning from 'components/MeetingEditComponents/OrgWideWarning'
import RequiredGuestAttributes from 'components/MeetingEditComponents/RequiredGuestAttributes'
import VideoConferencingSelect from 'components/MeetingEditComponents/VideoConferencingSelect'
import { MeetingNameWithBadge } from 'components/MeetingNameWithBadge'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import useDefaultSettings from 'hooks/useDefaultSettings'
import useMeetingTypesList from 'hooks/useMeetingTypesList'
import useNotification from 'hooks/useNotification'
import useOrgId from 'hooks/useOrgId'
import useProfilePath from 'hooks/useProfilePath'
import useUserId from 'hooks/useUserId'
import { slugify } from 'utils/index'
import { trackMeetingLinkCopied } from 'utils/tracking'

const BreadcrumbContainer = styled(Flex, {
  width: '100%',
})

const BreadcrumbLink = styled('a', {
  '&:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
})

const HeaderAndButton = styled(Flex, {
  width: '100%',
})

interface EditRouteState {
  meetingId: string
  isNewMeeting: boolean
  isOrgMeeting: boolean
  isDefault: boolean
  meetingName: string
}

const emptyOrgMeeting = {
  orgMeeting: true,
  guestNotifications: DEFAULT_GUEST_CONFIG,
  name: 'New shared meeting type',
  title: 'New shared meeting type',
  duration: 30,
  conferenceType: ConferenceProvider.None,
}

const emptyPersonalMeeting = {
  orgMeeting: false,
  guestNotifications: DEFAULT_GUEST_CONFIG,
  name: 'New meeting type',
  title: 'New meeting type',
  duration: 30,
  conferenceType: ConferenceProvider.None,
}

const emptyMeeting = (isOrgMeeting: boolean) =>
  isOrgMeeting ? emptyOrgMeeting : emptyPersonalMeeting

export default function Page() {
  //get our meeting
  const history = useHistory()
  const routeState = history.location.state as EditRouteState
  //get existing meeting
  const meetingId = routeState.meetingId
  const { meetingTypesById, isLoading: loadingMeetingsList } =
    useMeetingTypesList()
  // get defaults
  const { defaultSettings, isLoading: loadingDefault } = useDefaultSettings()
  // set meeting as existing or default
  // if meeting list is still loading, don't use default data yet
  const meeting = loadingMeetingsList
    ? meetingTypesById[meetingId]
    : meetingTypesById[meetingId] ?? emptyMeeting(routeState.isOrgMeeting)
  const isOrgMeeting = meeting?.orgMeeting || routeState.isOrgMeeting
  const isNewMeeting = routeState.isNewMeeting
  const isDefault = routeState.isDefault
  const orgId = useOrgId()
  const userId = useUserId()
  const profilesPath = useProfilePath()
  const defaultWidgetLocale: string = useSelector<any, string>((state) =>
    pathOr('en', ['embed', 'configuration', 'locale'], state)
  )

  //updated state to post
  const [updatedMeetingState, setUpdatedMeetingState] = useState(
    merge({ ...emptyMeeting(isOrgMeeting) }, { ...meeting })
  )
  useEffect(() => {
    setUpdatedMeetingState(meeting)
    setIsGroupMeeting(!!meeting?.agentAvailabilities)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMeetingsList])

  //implicit group meeting state
  const [isGroupMeeting, setIsGroupMeeting] = useState<boolean>(
    !!meeting?.agentAvailabilities
  )
  useEffect(() => {
    setIsGroupMeeting(!!updatedMeetingState?.agentAvailabilities)
  }, [updatedMeetingState?.agentAvailabilities])

  //on name change behavior for page title
  const onNameChange = (value: string) => {
    if (isNewMeeting) {
      history.replace('/new', {
        meetingId: routeState.meetingId,
        isNewMeeting: routeState.isNewMeeting,
        isOrgMeeting: routeState.isOrgMeeting,
        isDefault: routeState.isDefault,
        meetingName: value,
      })
    } else {
      history.replace(`/edit/${slugify(value)}`, {
        meetingId: routeState.meetingId,
        isNewMeeting: routeState.isNewMeeting,
        isOrgMeeting: routeState.isOrgMeeting,
        isDefault: routeState.isDefault,
        meetingName: value,
      })
    }
    setUpdatedMeetingState({
      ...updatedMeetingState,
      name: value,
    })
    if (value.length < 1) {
      setErrors({ ...errors, name: 'Internal meeting type name is required' })
    } else {
      setErrors({ ...errors, name: '' })
    }
    setIsDirty(true)
  }

  const onTitleChange = (value: string) => {
    setUpdatedMeetingState({
      ...updatedMeetingState,
      title: value,
    })
    if (value.length < 1) {
      setErrors({ ...errors, title: 'Meeting title is required' })
    } else {
      setErrors({ ...errors, title: '' })
    }
    setIsDirty(true)
  }

  //form errors
  const [isDirty, setIsDirty] = useState<boolean>(false)

  const [errors, setErrors] = useState({
    name: '',
    title: '',
    link: '',
    attendees: '',
  })

  const hasErrors = Object.values(errors).some(
    (errMessage) => errMessage !== ''
  )

  //meeting link already exists error
  useEffect(() => {
    if (updatedMeetingState) {
      let updatedMeetingLinkError = ''
      const updatedId = getSlug(isNewMeeting)
      const existingMeeting = meetingTypesById[updatedId]

      switch (true) {
        case isNewMeeting && !!existingMeeting:
        case !isNewMeeting && meetingId !== updatedId && !!existingMeeting:
          updatedMeetingLinkError = 'A meeting with that link already exists.'
      }
      setErrors({ ...errors, link: updatedMeetingLinkError })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewMeeting, meetingId, meetingTypesById, updatedMeetingState])

  //group meeting attendees error
  useEffect(() => {
    let updatedAttendeeError = ''
    const agentAvailabilities = updatedMeetingState?.agentAvailabilities
    const attendeeSelectIsDirty =
      JSON.stringify(meeting?.agentAvailabilities) !==
      JSON.stringify(agentAvailabilities)

    if (
      attendeeSelectIsDirty &&
      isGroupMeeting &&
      (!agentAvailabilities?.length || agentAvailabilities.length < 1)
    ) {
      updatedAttendeeError =
        'Group meetings must have at least one other attendee'
    }
    setErrors({ ...errors, attendees: updatedAttendeeError })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    errors.attendees,
    isGroupMeeting,
    meeting?.agentAvailabilities,
    updatedMeetingState?.agentAvailabilities,
  ])

  // meeting description -- update this ref separate from other state updates
  // to prevent entire page rerenders when editing description
  const meetingDescription = useRef(meeting?.inviteDescription)

  //user has edit access
  const hasOrgWideMeetingWriteAccess =
    ACCESS.isAdmin() ||
    ACCESS.hasFeatures([{ featureName: 'org_meetings', accessLevel: 'WRITE' }])
  const readOnly = !!isOrgMeeting && !hasOrgWideMeetingWriteAccess

  const getSlug = (isNewMeeting: boolean) => {
    if (!updatedMeetingState.slug || updatedMeetingState.slug === '') {
      if (isNewMeeting) {
        return slugify(updatedMeetingState.name)
      }
      return meetingId ?? slugify(updatedMeetingState.name)
    }
    return updatedMeetingState.slug
  }

  // save the meeting
  const notify = useNotification()
  const saveMeeting = async () => {
    const slug = getSlug(isNewMeeting)
    const oldSlug = meetingId
    let meetingTemplate = {
      default: routeState.isDefault,
      orgMeeting: !!isOrgMeeting,
      name: updatedMeetingState.name,
      title: updatedMeetingState.title,
      guestNotifications: updatedMeetingState.guestNotifications,
      duration: updatedMeetingState.duration,
      inviteDescription: meetingDescription.current,
      locale: updatedMeetingState.locale ?? '',
      location: updatedMeetingState.location ?? defaultWidgetLocale,
      conferenceLink: updatedMeetingState.conferenceLink,
      conferenceType: updatedMeetingState.conferenceType,
      requiredAttributes: updatedMeetingState.requiredAttributes ?? [],
      agentAvailabilities: updatedMeetingState.agentAvailabilities ?? [],
    }

    if (isDefault) {
      meetingTemplate = merge(
        { ...defaultSettings?.meetingTemplate },
        { ...meetingTemplate }
      )
    }

    const payload = {
      meetingTemplate,
      id: isOrgMeeting ? orgId : userId,
      slug: slug,
      isOrgMeeting: isOrgMeeting,
    }

    try {
      if (isNewMeeting) {
        await saveNewMeetingType(payload)
      } else {
        await updateMeetingType({ ...payload, oldSlug })
      }

      history.push(`/edit/${slug}`, {
        meetingId: slug,
        isNewMeeting: false,
        isOrgMeeting: !!isOrgMeeting,
        isDefault: routeState.isDefault,
        meetingName: updatedMeetingState.name,
      })
      notify({
        type: 'success',
        message: 'Your changes were saved!',
      })
      setIsDirty(false)
    } catch (e) {
      console.warn(e)
      notify({
        type: 'error',
        message: 'There was a problem saving your changes.',
      })
    }
  }

  const isLoading =
    loadingDefault && loadingMeetingsList && !updatedMeetingState
  const saveButtonsDisabled = (!isNewMeeting && !isDirty) || hasErrors
  if (!updatedMeetingState) {
    return <Skeleton isLoading={true} />
  } else {
    return (
      <Skeleton isLoading={isLoading}>
        {isOrgMeeting && hasOrgWideMeetingWriteAccess ? (
          <OrgWideWarning />
        ) : null}
        <Card>
          <Header variant="h3">Meeting type details</Header>
          <InputText
            size={'x-large'}
            label="Internal meeting type name"
            value={updatedMeetingState.name}
            description="This is used for internal purposes, such as organizing your meeting types"
            onChange={(e) => onNameChange(e.target.value)}
            disabled={readOnly}
            errorMessage={errors.name}
          />
          <InputText
            size={'x-large'}
            label="Meeting title"
            placeholder="Add a meeting title for your guests"
            value={updatedMeetingState.title}
            description="This is the meeting title your guests will see"
            onChange={(e) => {
              onTitleChange(e.target.value)
            }}
            disabled={readOnly}
            errorMessage={errors.title}
          />
          <DurationSelect
            defaultDuration={updatedMeetingState.duration}
            updateState={(duration) => {
              setUpdatedMeetingState({ ...updatedMeetingState, duration })
              setIsDirty(true)
            }}
            disabled={readOnly}
          />
          {!isOrgMeeting && !isDefault && (
            <AttendeeSelect
              agentAvailabilities={updatedMeetingState.agentAvailabilities}
              updateState={(agentAvailabilities) => {
                setUpdatedMeetingState({
                  ...updatedMeetingState,
                  agentAvailabilities,
                })
                setIsDirty(true)
              }}
              disabled={readOnly}
              errors={errors}
              setErrors={setErrors}
            />
          )}
          <MeetingDescriptionEditor
            defaultDescription={updatedMeetingState.inviteDescription}
            updateDescription={(description: string) => {
              meetingDescription.current = description
              setIsDirty(true)
            }}
            readOnly={readOnly}
          />
          <VideoConferencingSelect
            defaultConferenceType={
              updatedMeetingState.conferenceType ?? ConferenceProvider.None
            }
            updateState={(conferenceType) => {
              setUpdatedMeetingState({ ...updatedMeetingState, conferenceType })
              setIsDirty(true)
            }}
            disabled={readOnly}
            isOrgMeeting={isOrgMeeting}
          />
          <InputText
            size={'x-large'}
            label="Meeting location"
            value={updatedMeetingState.location}
            placeholder={'Add a room or location name'}
            onChange={(e) => {
              setUpdatedMeetingState({
                ...updatedMeetingState,
                location: e.target.value,
              })
              setIsDirty(true)
            }}
            disabled={readOnly}
          />
          <MeetingLinkInput
            meetingId={meetingId}
            meetingName={updatedMeetingState.name}
            profilesPath={profilesPath}
            readOnly
            error={errors.link}
            getSlug={() => getSlug(isNewMeeting)}
            updateState={(conferenceLink) => {
              setUpdatedMeetingState({
                ...updatedMeetingState,
                conferenceLink,
              })
              setIsDirty(true)
            }}
            setLinkError={(newError: string) =>
              setErrors({ ...errors, link: newError })
            }
          />
          <LanguageSelect
            locale={updatedMeetingState.locale}
            updateState={(locale) => {
              setUpdatedMeetingState({ ...updatedMeetingState, locale })
              setIsDirty(true)
            }}
            disabled={readOnly}
            defaultWidgetLocale={defaultWidgetLocale}
          />
          <RequiredGuestAttributes
            attributes={updatedMeetingState.requiredAttributes}
            updateRequiredAttributesState={(attributes: string[]) => {
              setUpdatedMeetingState({
                ...updatedMeetingState,
                requiredAttributes: attributes,
              })
              setIsDirty(true)
            }}
            disabled={readOnly}
          />
          <br />
          <Header variant="h3">Automated guest emails</Header>
          <GuestEmailSettings
            guestNotifications={updatedMeetingState.guestNotifications}
            updateState={(guestNotifications) => {
              setUpdatedMeetingState({
                ...updatedMeetingState,
                guestNotifications,
              })
              setIsDirty(true)
            }}
            disabled={readOnly}
            isOrgMeeting={isOrgMeeting}
          />
          {!readOnly && (
            <Flex alignItems="center" justifyContent="flex-end">
              {hasErrors && (
                <StatusMessage variant="error">
                  You must fix the above errors before saving
                </StatusMessage>
              )}

              <Button
                variant={'secondary'}
                onClick={() =>
                  confirm(
                    'Are you sure you want to leave this page? Any unsaved changes will be lost.'
                  ) && history.push('/')
                }
              >
                Cancel
              </Button>
              <Button
                variant={'primary'}
                onClick={() => saveMeeting()}
                disabled={saveButtonsDisabled}
              >
                Save
              </Button>
            </Flex>
          )}
        </Card>
      </Skeleton>
    )
  }
}

Page.Header = function PageHeader() {
  const history = useHistory()
  const routeState = history.location.state as EditRouteState
  const meetingId = routeState.meetingId
  const { meetingTypesById, isLoading } = useMeetingTypesList()
  const meeting = isLoading
    ? meetingTypesById[meetingId]
    : meetingTypesById[meetingId] ?? emptyMeeting(routeState.isOrgMeeting)

  const profilesPath = useProfilePath()
  const copyToClipboard = useCopyToClipboard()

  return (
    <Skeleton isLoading={isLoading}>
      <BreadcrumbContainer direction="column" gap={0}>
        <Text variant="metadata" color="$gray8">
          <BreadcrumbLink onClick={() => history.push('/')}>
            Meetings
          </BreadcrumbLink>{' '}
          <Icon icon={faAngleRight} size={'extra-small'} /> Meeting type details
        </Text>
        <HeaderAndButton alignItems="center" justifyContent="space-between">
          <Header variant="h1">
            <MeetingNameWithBadge
              meeting={meeting}
              pageTitle={routeState.meetingName}
            />
          </Header>
          <Button
            onClick={() => {
              trackMeetingLinkCopied()
              copyToClipboard(`${profilesPath}/meeting/${meetingId}`)
            }}
          >
            Copy meeting link
          </Button>
        </HeaderAndButton>
      </BreadcrumbContainer>
    </Skeleton>
  )
}
